import { formatBytes } from '@jsonfmt/core';
import { type KeyboardEvent, type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/ui/components/Button';
import { Dialog } from '@/ui/components/Dialog';
import { Icon } from '@/ui/components/Icon';

import { type ExampleDescriptor, type ExampleId, examples } from './examples';
import styles from './ExamplesDialog.module.css';

/**
 * The examples modal — docs/DESIGN.md §6.7, recon §1.6.
 *
 * MARKUP FIRST, because this is where the legacy dialog failed every requirement at once:
 * it was a `<div onClick={onClose}>` with no role, no `aria-modal`, no focus trap, no
 * Escape handler and no focus restoration, whose cards were `<div onClick>` rather than
 * buttons — invisible to a screen reader and unreachable from a keyboard.
 *
 * Here the shell is the shared `Dialog` (native `<dialog>` + `showModal()`, capture-phase
 * Tab trap, Escape, scrim click, focus restoration to the opener) and the list is
 * `<ul role="list"><li><button>`: no overlapping roles, no `role="option"` without a
 * `listbox`, no `aria-selected` on something that is not selectable. The explicit
 * `role="list"` is there because `list-style: none` removes list semantics in Safari/VoiceOver.
 *
 * NO BLUR AND NO GRADIENT ON THE METADATA. The legacy rendered a code preview under
 * `filter: blur(2.5px)` plus a fade overlay — decoration that makes the preview unreadable
 * on purpose. DESIGN §6.7 replaces it with right-aligned tabular mono facts, which is what a
 * developer actually chooses an example by.
 *
 * THE FACTS ARE NOT COMPUTED HERE. They are declared in `examples.ts` and checked against the
 * real datasets by `examples.test.ts`; reading them from the data would mean importing all
 * four datasets in order to draw a list of four names, which is exactly what the lazy
 * `import()` exists to avoid. Nothing in this component loads a dataset — selecting a row
 * does, through `loadExampleCommand`.
 *
 * ═══ A CLICK OR A TAP LOADS, AND NOTHING STANDS BETWEEN THE TWO ═══
 *
 * ADR-0101 §7.16 and §8.6. The only handler on the card is `onClick`; there is deliberately no
 * `onPointerDown`, no `onMouseDown` and no `preventDefault` anywhere on the way in, because
 * that is the one shape of bug that makes a row "highlight but not load": suppressing the
 * default action of a press also suppresses the click that the press would have produced.
 * Selection depends on neither focus nor hover — `:hover` exists only to say what will happen,
 * and lives inside `(hover: hover)` so a thumb never leaves a card stuck filled. The contract
 * is pinned by a real-mouse `mouse.move/down/up` test in `e2e/smoke.spec.ts` and by a real tap
 * in `e2e/mobile.spec.ts`, neither of which a synthetic `click()` would have caught.
 */

export interface ExamplesDialogProps {
  readonly open: boolean;
  /** Escape, the × control, the scrim, or Cancel. The owner holds the open state. */
  readonly onClose: () => void;
  /**
   * `⌘Z` or `Ctrl+Z`, already narrowed for THIS machine.
   *
   * It used to read `UNDO_KEYCAP` straight out of `document/commands.ts`, which states BOTH
   * spellings (`'Ctrl+Z / ⌘Z'`) because that file is compiled without `navigator` (gate 1)
   * and refuses to guess. The toast for the same action is narrowed on its way past by
   * `resolveKeycaps` in the composition root — so the dialog printed `Ctrl+Z / ⌘Z` while the
   * toast two seconds later printed `⌘Z`, for one keystroke. There is one substitution point
   * and this is it arriving as a prop, rather than a second exception to it.
   */
  readonly undoKeycap: string;
  /**
   * A row was activated. The owner runs `loadExampleCommand(id)` through `runCommand`, which
   * is what makes loading one undo step and closes the dialog via `closeDialogs`.
   */
  readonly onSelect: (id: ExampleId) => void;
}

export function ExamplesDialog({
  open,
  onClose,
  onSelect,
  undoKeycap,
}: ExamplesDialogProps): ReactNode {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  const firstRowRef = useRef<HTMLButtonElement>(null);

  /**
   * `↑ ↓ move`, `⏎ load` — the contract the footer prints, implemented (DESIGN §7.4).
   *
   * Enter and Space need no handler: the rows are real `<button>`s. Only the arrows do,
   * because moving between siblings is not something a button knows how to do; `Home` and
   * `End` come along for free and cost two lines.
   */
  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    const step =
      event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowUp'
          ? -1
          : event.key === 'Home'
            ? 0
            : null;
    if (step === null && event.key !== 'End') return;

    const rows = Array.from(listRef.current?.querySelectorAll('button') ?? []);
    if (rows.length === 0) return;

    const current = rows.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? rows.length - 1
          : // Wraps, so ↓ on the last row returns to the first rather than dead-ending.
            (current + (step ?? 0) + rows.length) % rows.length;

    rows[next]?.focus();
    // Only once a row was actually moved to: otherwise the dialog's own Tab trap and the
    // browser's scrolling keys would both be swallowed by a list that did nothing.
    event.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('examples.title')}
      closeLabel={t('action.close')}
      description={t('examples.subtitle', { shortcut: undoKeycap })}
      initialFocus={firstRowRef}
      // Violet is "discover" (ADR-0101 §1): the hue of the button that opened this dialog, of
      // its head tile and of every card in it. `examples` is the file-with-braces glyph.
      hue="violet"
      icon="examples"
      footer={
        <>
          <span className={styles.hint}>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t('examples.keyMove')}
          </span>
          <span className={styles.hint}>
            <kbd>⏎</kbd> {t('examples.keyLoad')}
          </span>
          <span className={styles.spacer} />
          <Button label={t('action.cancel')} hue="peri" onClick={onClose} />
        </>
      }
    >
      <ul className={styles.list} role="list" ref={listRef} onKeyDown={onListKeyDown}>
        {examples.map((example, index) => (
          <li key={example.id}>
            <button
              type="button"
              className={styles.row}
              ref={index === 0 ? firstRowRef : undefined}
              onClick={() => {
                onSelect(example.id);
              }}
            >
              {/* The shape channel of the card: a pale violet square that fills solid under
                  the pointer. Decorative — the name beside it carries the information. */}
              <span className={styles.icon} aria-hidden="true">
                <Icon name="examples" size={19} />
              </span>
              <span className={styles.name}>{t(`examples.item.${example.id}.name`)}</span>
              <span className={styles.description}>
                {t(`examples.item.${example.id}.description`)}
              </span>
              {/* Two lines of mono metadata, tabular — under the text on a phone, and a
                  right-aligned third column from 600px. The column a developer reads down. */}
              <span className={styles.facts}>
                {sizeLine(t, example)}
                <br />
                {shapeLine(t, example)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

function sizeLine(t: Translate, example: ExampleDescriptor): string {
  return t('examples.sizeLine', {
    size: formatBytes(example.facts.bytes),
    lines: example.facts.lines.toLocaleString(),
  });
}

function shapeLine(t: Translate, example: ExampleDescriptor): string {
  return t('examples.shapeLine', {
    depth: example.facts.depth,
    keys: example.facts.keys.toLocaleString(),
  });
}

export default ExamplesDialog;
